import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, ListChecks, Wallet, PawPrint, Images, ShoppingBasket, Crown, BellRing, Plus, Check, X, Hammer, Gamepad2, Gift } from "lucide-react";
import { useAct, useGet } from "../lib/api";
import { useAuth, useName } from "../lib/auth";
import { ymd, addDays, fmtDate, fmtTime, money, num, CATS, today } from "../lib/util";
import { SIZE_CLASS } from "../lib/theme";
import { Empty, Img, Sheet, Field, Badge } from "../components/kit";
import { UserLogo, CrownLogo, SunflowerLogo } from "../components/Logos";
import { SpendDonut } from "../components/Donut";

const W = ({ title, icon: Icon, to, size, children, testid, right }) => {
  const nav = useNavigate();
  const go = (e) => { if (to && !e.target.closest("button, a, input, label, textarea")) nav(to); };
  return (
    <section role="link" tabIndex={0} onClick={go} onKeyDown={(e) => e.key === "Enter" && to && nav(to)}
      className={`widget p-4 cursor-pointer ${SIZE_CLASS[size]} ${size === "large" ? "min-h-[260px]" : ""}`} data-testid={`widget-${testid}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-2 label-xs" data-testid={`widget-${testid}-open`}>
          {Icon && <Icon size={13} />} {title}
        </span>
        {right}
      </div>
      {children}
    </section>
  );
};
const lim = (size) => ({ small: 2, standard: 3, wide: 3, large: 6 }[size]);

function Greeting({ size }) {
  const { me } = useAuth();
  const p = useGet("/points");
  const h = new Date().getHours();
  const g = me.greeting || `${h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"}, ${me.display_name}`;
  const last = p.data?.months?.[0];
  return (
    <section className={`widget p-5 ${SIZE_CLASS[size]} relative overflow-hidden`} data-testid="widget-greeting">
      <div className="flex items-center gap-3">
        {me.avatar_media_id ? <Img id={me.avatar_media_id} thumb className="w-12 h-12 rounded-full" /> : <UserLogo id={me.id} size={44} />}
        <div className="min-w-0">
          <p className="label-xs">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
          <h2 className={`display ${size === "small" ? "text-2xl" : "text-3xl"} leading-tight`} data-testid="greeting-text">{g}</h2>
        </div>
      </div>
      {last?.winners?.includes(me.id) && <p className="mt-3 text-xs flex items-center gap-1 text-[#E0B354]" data-testid="greeting-crown"><Crown size={14} /> Champion of {last.month}</p>}
    </section>
  );
}

function Events({ size }) {
  const nav = useNavigate();
  const q = useGet(`/events?start=${today()}&end=${ymd(addDays(new Date(), 30))}`);
  const list = (q.data || []).slice(0, lim(size));
  return (
    <W title="Upcoming" icon={CalendarDays} to="/calendar" size={size} testid="events">
      {list.length === 0 ? <Empty text="No upcoming events." /> : list.map((e) => (
        <button key={e.id + e.occurrence_date} className="flex items-center gap-3 w-full text-left py-1.5" onClick={() => nav(`/calendar?date=${e.date}`)}>
          <span className="w-1 h-8 rounded-full" style={{ background: CATS[e.category]?.color }} />
          <span className="min-w-0"><span className="block text-sm truncate">{e.title}</span>
            <span className="text-xs text-oh-text2">{fmtDate(e.date, { weekday: "short", month: "short", day: "numeric" })}{e.time ? ` · ${fmtTime(e.time)}` : ""}</span></span>
        </button>
      ))}
    </W>
  );
}

function Chores({ size }) {
  const { me } = useAuth();
  const nav = useNavigate();
  const q = useGet("/chores");
  const t = useGet("/chore-counts/today").data || {};
  const appr = (q.data?.open || []).filter((c) => c.status === "awaiting" && c.completed_by !== me.id);
  const rows = [["alexis", <CrownLogo size={26} />], ["emily", <SunflowerLogo size={26} />],
    ["both", <span className="flex -space-x-2"><CrownLogo size={22} /><SunflowerLogo size={22} /></span>]];
  return (
    <W title="Chores" icon={ListChecks} to="/chores" size={size} testid="chores"
      right={appr.length > 0 && <button onClick={() => nav("/chores?tab=approvals")}><Badge tone="accent" testid="widget-chores-approvals">{appr.length} to approve</Badge></button>}>
      <div className="space-y-1.5">{rows.map(([k, logo]) => (
        <div key={k} className="flex items-center justify-between" data-testid={`widget-chores-row-${k}`}>
          <span className="flex items-center gap-2 text-xs text-oh-text2">{logo}{k === "both" && "Both"}</span>
          <span className="display text-2xl leading-none" data-testid={`widget-chores-count-${k}`}>{t[k] ?? 0}</span>
        </div>))}</div>
      <p className="text-[11px] text-oh-text2 mt-2">done today</p>
    </W>
  );
}

function Bills({ size }) {
  const s = useGet("/spending?filter=mine").data;
  return (
    <W title="Bills" icon={Wallet} to="/finances" size={size} testid="bills">
      {!s ? null : (
        <div className="flex items-center gap-3">
          <SpendDonut groups={s.groups} total={s.total_cents} size={size === "small" ? 64 : 84} stroke={9} label={false} testid="widget-bills-donut" />
          <div className="min-w-0">
            <p className="text-[11px] text-oh-text2">You've spent</p>
            <p className="display text-2xl leading-none" data-testid="widget-bills-spent">{money(s.total_cents)}</p>
            <p className="text-[11px] text-oh-text2 mt-1">this month{s.groups[0] ? ` · top: ${s.groups[0].label}` : ""}</p>
            {s.upcoming_unpaid_cents > 0 && <p className="text-[11px] text-oh-text2" data-testid="widget-bills-upcoming">{money(s.upcoming_unpaid_cents)} still due</p>}
            {s.overdue_cents > 0 && <p className="text-[11px] text-red-300" data-testid="widget-bills-overdue">{money(s.overdue_cents)} overdue</p>}
          </div>
        </div>
      )}
    </W>
  );
}

function Reminders({ size }) {
  const { me } = useAuth();
  const act = useAct();
  const q = useGet("/reminders");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const list = (q.data?.incoming || []).slice(0, lim(size) + 2);
  const left = (r) => Math.max(0, Math.ceil(24 - (Date.now() - new Date(r.ignored_at)) / 3600000));
  const sheet = (
    <Sheet open={open} onClose={() => setOpen(false)} title={`Remind ${me.partner_name}`} testid="reminder-sheet">
      <Field label="Reminder"><textarea className="inp" rows={3} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} data-testid="reminder-text-input" /></Field>
      <button className="btn w-full" disabled={!text.trim()} data-testid="reminder-send-button"
        onClick={async () => { await act("post", "/reminders", { text }, { ok: "Reminder sent" }); setText(""); setOpen(false); }}>Send reminder</button>
    </Sheet>
  );
  if (!list.length) return <>{sheet}<button className="hidden" id="reminder-new-global" onClick={() => setOpen(true)} /></>;
  return (
    <W title="Partner reminders" icon={BellRing} size={size} testid="reminders"
      right={<button className="icon-btn !w-8 !h-8" onClick={() => setOpen(true)} data-testid="reminder-new-button" aria-label="New reminder"><Plus size={16} /></button>}>
      {list.map((r) => (
        <div key={r.id} className="py-2 border-b border-oh-border last:border-0" data-testid={`reminder-${r.id}`}>
          <p className="text-sm">{r.text}</p>
          {r.status === "ignored" && <p className="text-[11px] text-oh-text2">Hides in {left(r)}h</p>}
          <div className="flex gap-2 mt-2">
            <button className="btn btn-sm" onClick={() => act("post", `/reminders/${r.id}/act`, { action: "done" }, { ok: "Done" })} data-testid={`reminder-done-${r.id}`}><Check size={13} className="inline" /> Done</button>
            <button className="btn btn-sm btn-ghost" onClick={() => act("post", `/reminders/${r.id}/act`, { action: "chore" }, { ok: "Chore created" })} data-testid={`reminder-chore-${r.id}`}><Hammer size={13} className="inline" /> Chore</button>
            {r.status === "active" && <button className="btn btn-sm btn-ghost" onClick={() => act("post", `/reminders/${r.id}/act`, { action: "ignore" })} data-testid={`reminder-ignore-${r.id}`}><X size={13} className="inline" /> Ignore</button>}
          </div>
        </div>
      ))}
      {sheet}
    </W>
  );
}

function Sparrow({ size }) {
  const q = useGet("/sparrow/today");
  const rs = q.data?.routines || [];
  const done = rs.filter((r) => r.log?.status === "done").length;
  return (
    <W title="Sparrow" icon={PawPrint} to="/sparrow" size={size} testid="sparrow">
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-2xl shrink-0 flex items-center justify-center bg-oh-sel" data-testid="widget-sparrow-logo"><PawPrint size={26} /></span>
        <div>
          <p className="display text-3xl leading-none" data-testid="widget-sparrow-progress">{done}/{rs.length}</p>
          <p className="text-xs text-oh-text2">care done today</p>
        </div>
      </div>
      {size !== "small" && rs.slice(0, lim(size)).map((r) => (
        <p key={r.id} className="text-xs mt-1.5 flex justify-between"><span>{r.name}</span><span className="text-oh-text2">{r.log?.status === "done" ? "Done" : r.log?.status === "missed" ? "Missed" : fmtTime(r.time) || "Anytime"}</span></p>
      ))}
    </W>
  );
}

function Gallery({ size }) {
  const nav = useNavigate();
  const { me } = useAuth();
  const picks = me.gallery_widget_media_ids || [];
  const q = useGet("/gallery?limit=3");
  const posts = q.data || [];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (picks.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % picks.length), 6000);
    return () => clearInterval(t);
  }, [picks.length]);
  const th = (p) => (p.media[0].type === "video" ? p.media[0].thumb_media_id : p.media[0].media_id);
  const h = size === "large" ? "h-64" : "h-44";
  if (picks.length) return (
    <W title="Gallery" icon={Images} to="/gallery" size={size} testid="gallery">
      <button className={`relative block w-full rounded-2xl overflow-hidden ${h}`} onClick={() => nav("/gallery")} data-testid="widget-gallery-custom">
        {[picks[i % picks.length], picks[(i + 1) % picks.length]].filter((v, k, a) => a.indexOf(v) === k).map((id, k) => (
          <Img key={id} id={id} thumb className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${k === 0 ? "opacity-100" : "opacity-0"}`} />))}
        {picks.length > 1 && <span className="absolute bottom-2 right-2 text-[10px] bg-black/50 rounded-full px-2 py-0.5">{(i % picks.length) + 1}/{picks.length}</span>}
      </button>
    </W>
  );
  return (
    <W title="Gallery" icon={Images} to="/gallery" size={size} testid="gallery">
      {posts.length === 0 ? <Empty text="No photos yet. Share the first memory." /> : (
        <button className={`grid grid-cols-3 gap-[2px] w-full rounded-2xl overflow-hidden ${h}`} onClick={() => nav("/gallery")} data-testid="widget-gallery-collage">
          <Img id={th(posts[0])} thumb className="col-span-2 row-span-2 w-full h-full" />
          <div className="grid grid-rows-2 gap-[2px] h-full">
            {[1, 2].map((i) => posts[i] ? <Img key={i} id={th(posts[i])} thumb className="w-full h-full" /> : <div key={i} className="bg-oh-card" />)}
          </div>
        </button>
      )}
    </W>
  );
}

function Score({ size }) {
  const nav = useNavigate();
  const name = useName();
  const p = useGet("/points");
  const a = useGet("/arcade");
  const active = (a.data?.challenges || []).filter((c) => ["active", "disputed", "pending"].includes(c.status));
  const m = p.data?.monthly || {};
  return (
    <section className={`${SIZE_CLASS[size]} grid grid-cols-2 gap-3`} data-testid="widget-scoreboard">
      <button className="widget p-4 text-left" onClick={() => nav("/scoreboard")} data-testid="widget-scoreboard-points">
        <p className="label-xs mb-2 flex items-center gap-1"><Crown size={12} /> Household</p>
        {["alexis", "emily"].map((u) => (
          <div key={u} className="flex items-center justify-between py-1"><span className="flex items-center gap-2 text-sm"><UserLogo id={u} size={20} ring={false} />{name(u)}</span>
            <span className="display text-xl" data-testid={`widget-points-${u}`}>{num(m[u])}</span></div>
        ))}
        <p className="text-[11px] text-oh-text2 mt-1">This month</p>
      </button>
      <button className="widget arcade-widget p-4 text-left" onClick={() => nav("/arcade")} data-testid="widget-scoreboard-arcade">
        <p className="retro-title text-[9px] mb-2 flex items-center gap-1.5 text-[#FFE45C]"><Gamepad2 size={12} /> ARCADE</p>
        {["alexis", "emily"].map((u) => (
          <div key={u} className="flex items-center justify-between py-0.5"><span className="retro-title text-[8px] text-[#39FF14]">{name(u).slice(0, 3).toUpperCase()}</span>
            <span className="retro-title text-xs" data-testid={`widget-arcade-balance-${u}`}>{num(a.data?.balances?.[u]?.total ?? 0)}</span></div>))}
        <p className="retro-title text-[8px] text-[#FF2BD6] mt-2 blink" data-testid="widget-arcade-active">{active.length ? `${active.length} CHALLENGE${active.length > 1 ? "S" : ""}` : "INSERT COIN"}</p>
        {active.slice(0, size === "large" ? 3 : 1).map((c) => <p key={c.id} className="text-[11px] mt-1 truncate">{c.title}</p>)}
      </button>
    </section>
  );
}

function Shopping({ size }) {
  const q = useGet("/shopping");
  const open = q.data?.open || [];
  return (
    <W title="Shopping" icon={ShoppingBasket} to="/shopping" size={size} testid="shopping">
      {open.length === 0 ? <Empty text="Everything is stocked." /> : (
        <>
          <p className="display text-3xl leading-none">{open.length}</p>
          <p className="text-xs text-oh-text2 mb-1">low or out</p>
          {size !== "small" && open.slice(0, lim(size)).map((i) => <p key={i.id} className="text-sm truncate">· {i.name}</p>)}
        </>
      )}
    </W>
  );
}

function Wishlist({ size }) {
  const name = useName();
  const { me } = useAuth();
  const s = useGet("/wishlist/summary").data;
  return (
    <W title="Wishlist" icon={Gift} to="/wishlist" size={size} testid="wishlist">
      {!s ? null : (
        <>
          <p className="display text-3xl leading-none" data-testid="widget-wishlist-count">{s.mine + s.theirs + s.ours}</p>
          <p className="text-xs text-oh-text2 mb-1">things we want{s.total_cents ? ` · ~${money(s.total_cents)}` : ""}</p>
          {size !== "small" && <p className="text-xs text-oh-text2" data-testid="widget-wishlist-split">Mine {s.mine} · {name(me.partner)} {s.theirs} · Ours {s.ours}</p>}
        </>
      )}
    </W>
  );
}

function CalendarW({ size }) {
  const nav = useNavigate();
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const start = addDays(first, -((first.getDay() + 6) % 7));
  const q = useGet(`/events?start=${ymd(start)}&end=${ymd(addDays(start, 41))}`);
  const days = new Set((q.data || []).map((e) => e.date));
  return (
    <W title={d.toLocaleDateString(undefined, { month: "long", year: "numeric" })} icon={CalendarDays} to="/calendar" size={size} testid="calendar">
      <div className="grid grid-cols-7 text-center gap-y-0.5 text-xs">
        {["M", "T", "W", "T", "F", "S", "S"].map((w, i) => <span key={i} className="label-xs !text-[9px]">{w}</span>)}
        {Array.from({ length: 42 }).map((_, i) => { const x = addDays(start, i), ds = ymd(x); return (
          <button key={ds} onClick={() => nav(`/calendar?date=${ds}`)} className={`h-7 rounded-lg flex flex-col items-center justify-center ${x.getMonth() !== d.getMonth() ? "opacity-25" : ""} ${ds === today() ? "bg-oh-sel text-oh-nav font-bold" : ""}`}>
            {x.getDate()}<span className={`w-1 h-1 rounded-full ${days.has(ds) ? "bg-oh-accent" : ""}`} /></button>); })}
      </div>
    </W>
  );
}

function UpcomingBills({ size }) {
  const nav = useNavigate();
  const q = useGet("/bills/upcoming");
  const list = (q.data || []).filter((b) => b.due_date.slice(0, 7) <= today().slice(0, 7)).slice(0, lim(size) + 1);
  return (
    <W title="Upcoming bills" icon={Wallet} to="/finances" size={size} testid="upcoming-bills">
      {list.length === 0 ? <Empty text="No unpaid bills." /> : list.map((b) => (
        <button key={b.id} className="flex items-center justify-between w-full py-1.5 text-left" onClick={() => nav(`/finances/${b.id}`)} data-testid={`upcoming-bill-${b.id}`}>
          <span className="text-sm truncate">{b.title}</span>
          <span className="text-xs shrink-0 ml-2">{b.status === "overdue" ? <Badge tone="bad">Overdue</Badge> : <span className="text-oh-text2">{fmtDate(b.due_date)}</span>} <b className="ml-1">{money(b.remaining_cents)}</b></span>
        </button>))}
    </W>
  );
}

const MAP = { calendar: CalendarW, upcoming_bills: UpcomingBills, events: Events, chores: Chores, bills: Bills, reminders: Reminders, sparrow: Sparrow, gallery: Gallery, scoreboard: Score, shopping: Shopping, wishlist: Wishlist };

export function Dashboard({ layout }) {
  return (
    <div className="grid grid-cols-2 gap-3 stagger" data-testid="dashboard-grid">
      {layout.filter((w) => MAP[w.id]).map((w) => { const C = MAP[w.id]; return <C key={w.id} size={w.size} />; })}
    </div>
  );
}

export default function Home() {
  const { me } = useAuth();
  return (
    <>
      <Dashboard layout={me.layout} />
      <button className="btn btn-ghost btn-sm mt-4 mx-auto flex items-center gap-1" onClick={() => document.getElementById("reminder-new-global")?.click() || document.querySelector("[data-testid=reminder-new-button]")?.click()} data-testid="home-remind-partner"><BellRing size={13} /> Remind {me.partner_name}</button>
    </>
  );
}
